import React from 'react';
import { Palette, Type, Layout as LayoutIcon, Image as ImageIcon } from 'lucide-react';
import type { TemplateDefinition } from '../../../types/outreach';
import { FONT_OPTIONS, LAYOUT_TEMPLATES } from '../../../constants/outreach';
import LogoPositionSelector from '../LogoPositionSelector';

interface Props {
  definition: TemplateDefinition;
  onChange: (definition: TemplateDefinition) => void;
  organizationLogoUrl?: string | null;
  workspaceLogoUrl?: string | null;
}

const DesignControls: React.FC<Props> = ({
  definition,
  onChange,
  organizationLogoUrl,
  workspaceLogoUrl
}) => {
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2">
          Design & Branding
        </h2>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          Customize the look and feel of your public outreach page
        </p>
      </div>

      {/* Logo Configuration */}
      <div className="card p-6">
        <h4 className="font-medium text-text-primary dark:text-text-dark-primary mb-4 flex items-center gap-2">
          <ImageIcon className="w-4 h-4" />
          Logo Configuration
        </h4>
        <div className="space-y-4 pl-6">
          <LogoPositionSelector
            enabled={definition.branding?.logos?.organization?.enabled || false}
            position={definition.branding?.logos?.organization?.position || 'header_center'}
            logoUrl={organizationLogoUrl || definition.branding?.logos?.organization?.url || null}
            label="Organization Logo"
            onEnabledChange={(enabled) => onChange({
              ...definition,
              branding: {
                ...definition.branding,
                logos: {
                  ...definition.branding?.logos,
                  organization: {
                    enabled,
                    url: organizationLogoUrl || definition.branding?.logos?.organization?.url || null,
                    position: definition.branding?.logos?.organization?.position || 'header_center'
                  }
                }
              }
            })}
            onPositionChange={(position) => onChange({
              ...definition,
              branding: {
                ...definition.branding,
                logos: {
                  ...definition.branding?.logos,
                  organization: {
                    enabled: definition.branding?.logos?.organization?.enabled || true,
                    url: organizationLogoUrl || definition.branding?.logos?.organization?.url || null,
                    position
                  }
                }
              }
            })}
          />

          <LogoPositionSelector
            enabled={definition.branding?.logos?.workspace?.enabled || false}
            position={definition.branding?.logos?.workspace?.position || 'header_left'}
            logoUrl={workspaceLogoUrl || definition.branding?.logos?.workspace?.url || null}
            label="Workspace Logo"
            onEnabledChange={(enabled) => onChange({
              ...definition,
              branding: {
                ...definition.branding,
                logos: {
                  ...definition.branding?.logos,
                  workspace: {
                    enabled,
                    url: workspaceLogoUrl || definition.branding?.logos?.workspace?.url || null,
                    position: definition.branding?.logos?.workspace?.position || 'header_left'
                  }
                }
              }
            })}
            onPositionChange={(position) => onChange({
              ...definition,
              branding: {
                ...definition.branding,
                logos: {
                  ...definition.branding?.logos,
                  workspace: {
                    enabled: definition.branding?.logos?.workspace?.enabled || true,
                    url: workspaceLogoUrl || definition.branding?.logos?.workspace?.url || null,
                    position
                  }
                }
              }
            })}
          />
        </div>
      </div>

      {/* Colors */}
      <div className="card p-6">
        <h4 className="font-medium text-text-primary dark:text-text-dark-primary mb-4 flex items-center gap-2">
          <Palette className="w-4 h-4" />
          Colors
        </h4>
        <div className="grid grid-cols-2 gap-4 pl-6">
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
              Primary Color
            </label>
            <input
              type="color"
              value={definition.branding?.colors?.primary || '#2563eb'}
              onChange={(e) => onChange({
                ...definition,
                branding: {
                  ...definition.branding,
                  colors: {
                    primary: e.target.value,
                    secondary: definition.branding?.colors?.secondary || '#64748b',
                    background: definition.branding?.colors?.background || '#ffffff',
                    text: definition.branding?.colors?.text || '#1e293b'
                  }
                }
              })}
              className="w-full h-10 rounded border border-border-light dark:border-border-dark cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
              Secondary Color
            </label>
            <input
              type="color"
              value={definition.branding?.colors?.secondary || '#64748b'}
              onChange={(e) => onChange({
                ...definition,
                branding: {
                  ...definition.branding,
                  colors: {
                    primary: definition.branding?.colors?.primary || '#2563eb',
                    secondary: e.target.value,
                    background: definition.branding?.colors?.background || '#ffffff',
                    text: definition.branding?.colors?.text || '#1e293b'
                  }
                }
              })}
              className="w-full h-10 rounded border border-border-light dark:border-border-dark cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
              Background Color
            </label>
            <input
              type="color"
              value={definition.branding?.colors?.background || '#ffffff'}
              onChange={(e) => onChange({
                ...definition,
                branding: {
                  ...definition.branding,
                  colors: {
                    primary: definition.branding?.colors?.primary || '#2563eb',
                    secondary: definition.branding?.colors?.secondary || '#64748b',
                    background: e.target.value,
                    text: definition.branding?.colors?.text || '#1e293b'
                  }
                }
              })}
              className="w-full h-10 rounded border border-border-light dark:border-border-dark cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
              Text Color
            </label>
            <input
              type="color"
              value={definition.branding?.colors?.text || '#1e293b'}
              onChange={(e) => onChange({
                ...definition,
                branding: {
                  ...definition.branding,
                  colors: {
                    primary: definition.branding?.colors?.primary || '#2563eb',
                    secondary: definition.branding?.colors?.secondary || '#64748b',
                    background: definition.branding?.colors?.background || '#ffffff',
                    text: e.target.value
                  }
                }
              })}
              className="w-full h-10 rounded border border-border-light dark:border-border-dark cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Typography */}
      <div className="card p-6">
        <h4 className="font-medium text-text-primary dark:text-text-dark-primary mb-4 flex items-center gap-2">
          <Type className="w-4 h-4" />
          Typography
        </h4>
        <div className="grid grid-cols-2 gap-4 pl-6">
          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
              Heading Font
            </label>
            <select
              value={definition.branding?.fonts?.heading || 'Inter'}
              onChange={(e) => onChange({
                ...definition,
                branding: {
                  ...definition.branding,
                  fonts: {
                    heading: e.target.value,
                    body: definition.branding?.fonts?.body || 'Inter'
                  }
                }
              })}
              className="input w-full"
            >
              {FONT_OPTIONS.map(font => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
              Body Font
            </label>
            <select
              value={definition.branding?.fonts?.body || 'Inter'}
              onChange={(e) => onChange({
                ...definition,
                branding: {
                  ...definition.branding,
                  fonts: {
                    heading: definition.branding?.fonts?.heading || 'Inter',
                    body: e.target.value
                  }
                }
              })}
              className="input w-full"
            >
              {FONT_OPTIONS.map(font => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Layout Template */}
      <div className="card p-6">
        <h4 className="font-medium text-text-primary dark:text-text-dark-primary mb-4 flex items-center gap-2">
          <LayoutIcon className="w-4 h-4" />
          Layout Template
        </h4>
        <div className="grid grid-cols-2 gap-3 pl-6">
          {LAYOUT_TEMPLATES.map(template => (
            <label
              key={template.value}
              className={`
                flex flex-col p-4 border rounded-lg cursor-pointer transition-all
                ${definition.branding?.layout_template === template.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-border-light dark:border-border-dark hover:border-primary-300'
                }
              `}
            >
              <input
                type="radio"
                name="layout_template"
                value={template.value}
                checked={definition.branding?.layout_template === template.value}
                onChange={(e) => onChange({
                  ...definition,
                  branding: {
                    ...definition.branding,
                    layout_template: e.target.value as 'modern' | 'corporate' | 'minimal' | 'branded'
                  }
                })}
                className="sr-only"
              />
              <span className="font-medium text-text-primary dark:text-text-dark-primary">
                {template.label}
              </span>
              <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                {template.description}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Custom HTML (Advanced) */}
      <div className="card p-6">
        <details>
          <summary className="font-medium text-text-primary dark:text-text-dark-primary cursor-pointer mb-4">
            Advanced: Custom HTML (Optional)
          </summary>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                Custom Header HTML
              </label>
              <textarea
                value={definition.branding?.custom_header_html || ''}
                onChange={(e) => onChange({
                  ...definition,
                  branding: {
                    ...definition.branding,
                    custom_header_html: e.target.value || null
                  }
                })}
                placeholder="<div>Custom header content...</div>"
                className="input w-full min-h-[80px] font-mono text-sm"
              />
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                HTML will be sanitized for security
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                Custom Footer HTML
              </label>
              <textarea
                value={definition.branding?.custom_footer_html || ''}
                onChange={(e) => onChange({
                  ...definition,
                  branding: {
                    ...definition.branding,
                    custom_footer_html: e.target.value || null
                  }
                })}
                placeholder="<div>Custom footer content...</div>"
                className="input w-full min-h-[80px] font-mono text-sm"
              />
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                HTML will be sanitized for security
              </p>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
};

export default DesignControls;
